import * as React from 'react';
import styled from 'styled-components';
import { useSearchParam } from 'react-use';
import { getJourneyStudioUrl } from 'utils/appRoutes';
import { SearchableText } from 'ui-beta/searchableText';
import { GhostButton } from 'ui/button';
import {
  ARROW_DIRECTION_DOWN,
  CollapsibleContent,
  CollapsibleContentArrow,
} from 'ui/collapsibleContent';
import { AiColor16 } from 'ui/icons';
import { Link } from 'ui/links';
import { TagGroup } from 'ui/tags';
import { colors, fontSizes, spacing } from 'ui/theme';
import { Skeleton } from 'ui/skeleton';
import { Tooltip } from 'ui/tooltip';
import { pxToRem } from 'utils/convert';
import { BUTTON_SIZES } from 'ui/button/constants';
import { QUERY_PARAMS } from 'modules/workflowIndex/constants';

import {
  WorkflowNameData,
  WorkflowIdData,
  WorkflowDescriptionData,
  WorkflowLabelData,
  WorkflowNodeCampaign,
  WorkflowTypeData,
  WorkflowTimestampData,
  WorkflowVisitorTracking,
} from '../../../types';

import { useWorkflowCampaignNodeMetadata } from '../../../hooks/useWorkflowCampaignNodeMetadata';

import { TableCell } from '../TableCell';

import { CampaignsList, CampaignsListSkeleton } from './CampaignsList';
import {
  workflowCampaignUsesFrequencyOptimization,
  workflowCampaignUsesSendTimeOptimization,
} from './utils';

const { useState, useCallback } = React;

const displayText = {
  id: 'ID',
  campaign: 'campaign',
  campaigns: 'campaigns',
  sendTimeOptimized: 'One or more campaigns are send time optimized',
  frequencyOptimized: 'One or more campaigns are frequency optimized',
  sendTimeOrFrequencyOptimized: 'Some campaigns are send time or frequency optimized',
};

const getDataScienceOptimizationTooltipText = (
  usesSendTimeOptimization: boolean,
  usesFrequencyOptimization: boolean,
) => {
  if (usesSendTimeOptimization && usesFrequencyOptimization) {
    return displayText.sendTimeOrFrequencyOptimized;
  } else if (usesSendTimeOptimization) {
    return displayText.sendTimeOptimized;
  } else if (usesFrequencyOptimization) {
    return displayText.frequencyOptimized;
  }
  return '';
};

const StyledCollapsibleContent = styled(CollapsibleContent)`
  grid-column: 2 / span 1;
  grid-row: 2 / span 1;
`;

const CellContents = styled.div`
  width: 100%;
`;

const TopBar = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-template-columns: 1fr minmax(${pxToRem(20)}, max-content) minmax(min-content, max-content) minmax(
      min-content,
      max-content
    );
  grid-template-rows: min-content min-content;
  gap: 0px 5px;
  grid-template-areas:
    'name aiIcon campaignCount expandButton'
    'idAndLabels aiIcon campaignCount expandButton';
  width: 100%;
`;

const WorkflowIdAndLabels = styled.div`
  display: flex;
  align-items: baseline;
  grid-area: idAndLabels;
`;

const WorkflowId = styled.div`
  font-size: ${fontSizes.s12};
  color: ${colors.textSecondary};
`;
const JourneyEventTracking = styled.div`
  font-size: ${fontSizes.s12};
  color: ${colors.textSecondary};
`;

const Bullet = styled.div`
  margin: 0 ${spacing.half};
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s12};
  :after { 
    content: '•';
  }
`;

const ArrowButton = styled(GhostButton)`
  cursor: pointer;
  align-self: center;
  grid-area: expandButton;
`;

const WorkflowCampaignCount = styled.div`
  color: ${colors.textSecondary};
  margin-right: ${spacing.quarter};
  white-space: nowrap;
  align-self: center;
  grid-area: campaignCount;
`;

const WorkflowDescription = styled.p`
  color: ${colors.textSecondary};
  padding: ${spacing.half} 0;
`;

const DataScienceOptimizationIconContainer = styled.div`
  grid-area: aiIcon;
  align-self: center;
  margin-top: ${spacing.quarter};
`;

const WorkflowCampaignCountLoadingSkeleton = styled(Skeleton)`
  grid-area: campaignCount;
  width: ${spacing.x7};
  height: ${fontSizes.s16};
  background-color: ${colors.grey5};
  margin-right: ${spacing.half};
  align-self: center;
`;

type WorkflowNameCellProps = {
  workflow: WorkflowNameData &
    WorkflowIdData &
    WorkflowDescriptionData &
    WorkflowLabelData &
    WorkflowTypeData &
    WorkflowTimestampData &
    WorkflowVisitorTracking;
};

export const WorkflowNameCell = ({ workflow }: WorkflowNameCellProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleIsExpanded = useCallback(() => setIsExpanded(!isExpanded), [isExpanded]);
  const workflowCampaignNodeMetadata = useWorkflowCampaignNodeMetadata({
    workflowId: workflow.id,
    workflowType: workflow.workflowType,
  });
  const campaignsInWorkflowNodes = workflowCampaignNodeMetadata.allNodes
    .map(({ resource }) => resource?.campaign)
    .filter<WorkflowNodeCampaign>((campaign): campaign is WorkflowNodeCampaign => !!campaign);
  const canExpand = !!workflow.description?.length || !!campaignsInWorkflowNodes.length;
  const usesSendTimeOptimization = campaignsInWorkflowNodes.some(
    workflowCampaignUsesSendTimeOptimization,
  );
  const usesFrequencyOptimization = campaignsInWorkflowNodes.some(
    workflowCampaignUsesFrequencyOptimization,
  );
  const searchText = useSearchParam(QUERY_PARAMS.SEARCH) || '';

  return (
    <>
      <TableCell data-test="workflow-name-cell">
        <CellContents>
          <TopBar>
            <Link
              inTable
              data-test="workflow-name-link"
              href={getJourneyStudioUrl(workflow.id, workflow.workflowType)}
            >
              <SearchableText
                isCaseSensitive={false}
                text={workflow.name}
                searchText={searchText}
              />
            </Link>
            <WorkflowIdAndLabels>
              <WorkflowId data-test="workflow-id">
                {displayText.id}: {workflow.id}
              </WorkflowId>

              {workflow?.visitorTrackingEnabled && (
                <>
                  <Bullet />
                  <JourneyEventTracking data-test="workflow-journey-event-tracking">
                    Tracking journey events
                  </JourneyEventTracking>
                </>
              )}
              {(workflow?.labels || [])?.length > 0 && (
                <>
                  <Bullet />
                  <TagGroup
                    value={workflow.labels?.map(({ name }) => name) || []}
                    maxTags={2}
                    isEditable={false}
                  />
                </>
              )}
            </WorkflowIdAndLabels>
            {canExpand &&
              !!campaignsInWorkflowNodes.length &&
              (usesSendTimeOptimization || usesFrequencyOptimization) && (
                <DataScienceOptimizationIconContainer>
                  <Tooltip
                    tooltipContent={getDataScienceOptimizationTooltipText(
                      usesSendTimeOptimization,
                      usesFrequencyOptimization,
                    )}
                  >
                    <AiColor16 data-test="aggregate-data-science-optimization-icon" />
                  </Tooltip>
                </DataScienceOptimizationIconContainer>
              )}
            {workflowCampaignNodeMetadata.isLoading && (
              <WorkflowCampaignCountLoadingSkeleton data-test="campaign-count-skeleton" />
            )}
            {!workflowCampaignNodeMetadata.isLoading &&
              !workflowCampaignNodeMetadata.didError &&
              !!campaignsInWorkflowNodes.length && (
                <WorkflowCampaignCount data-test="workflow-campaign-count">
                  {campaignsInWorkflowNodes.length.toLocaleString()}{' '}
                  {campaignsInWorkflowNodes.length === 1
                    ? displayText.campaign
                    : displayText.campaigns}
                </WorkflowCampaignCount>
              )}
            {canExpand && (
              <ArrowButton onClick={toggleIsExpanded} size={BUTTON_SIZES.SMALL}>
                <CollapsibleContentArrow
                  isCollapsed={!canExpand || !isExpanded}
                  arrowStart={ARROW_DIRECTION_DOWN}
                  arrowEnd={180}
                  width={16}
                  height={16}
                />
              </ArrowButton>
            )}
          </TopBar>
        </CellContents>
      </TableCell>
      <StyledCollapsibleContent
        data-test="workflow-collapsible-content"
        isCollapsed={!canExpand || !isExpanded}
      >
        <WorkflowDescription data-test="workflow-description">
          {workflow.description}
        </WorkflowDescription>
        {workflowCampaignNodeMetadata.isLoading && <CampaignsListSkeleton />}
        {!workflowCampaignNodeMetadata.isLoading && !!campaignsInWorkflowNodes.length && (
          <CampaignsList
            workflow={workflow}
            workflowCampaignNodeMetadata={workflowCampaignNodeMetadata}
            isVisible={isExpanded}
          />
        )}
      </StyledCollapsibleContent>
    </>
  );
};
